/**
 * AP-34 12.3, 12.4 — **Zahlungsläufe und Kontoauszug**: Vorschlag → Lauf → Freigabe → SEPA-Datei →
 * ausgeführt; dazu die Rückmeldung aus dem Kontoauszug mit ihrer Klärungsliste.
 *
 * Eine Arbeitsseite, keine Auswertungsliste: Jeder Schritt ist eine Handlung mit ihrem Knopf.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Trash2 } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { pageWide } from '@/lib/form-styles'
import { Abschnitt, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { assignBankEntry, createPaymentRun, downloadRunFile, fetchPaymentRun, fetchPaymentRuns, fetchRunProposal, patchRunItem, removeRunItem, runAction, uploadBankStatement } from '../api/incoming-invoices.api'
import { tagText } from '../lib/texte'
import type { BankImportResult, PaymentRun, PaymentRunItem, RunProposal } from '../types/incoming-invoices.types'

const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const haupt = 'rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'
const iso = (d: Date) => d.toISOString().slice(0, 10)
const inTagen = (n: number) => iso(new Date(Date.now() + n * 86_400_000))

const LAUF_STATUS: Record<string, { label: string; klasse: string }> = {
  DRAFT: { label: 'Entwurf', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  RELEASED: { label: 'freigegeben', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  FILE_CREATED: { label: 'Datei erzeugt', klasse: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  COMPLETED: { label: 'ausgeführt', klasse: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  CANCELLED: { label: 'verworfen', klasse: 'bg-muted text-muted-foreground' },
}
const POSTEN_STATUS: Record<string, string> = { PLANNED: 'geplant', CONFIRMED: 'bezahlt', RETURNED: 'Rückläufer' }
const AUSNAHME: Record<string, string> = {
  BLOCKED: 'Zahlung gesperrt',
  NO_IBAN: 'keine gültige IBAN',
  IN_RUN: 'steht schon in einem Zahlungslauf',
  RETAINED: 'der Rest ist als Rücklass einbehalten',
  HFU_CHECK_MISSING: 'Auftraggeberhaftung — für den Ausführungstag fehlt die HFU-Prüfung',
  AGH_NO_SERVICE_CENTER: 'nicht auf der HFU-Liste — in den Einstellungen fehlt das Konto des Dienstleistungszentrums',
  CURRENCY: 'andere Währung als Euro',
}

export default function IncomingPaymentRunsPage() {
  const { user } = useAuthStore()
  const [param, setParam] = useSearchParams()
  const darfAnlegen = erfuelltRecht('incoming_invoices.payment_run.create', user?.permissions)
  const darfFreigeben = erfuelltRecht('incoming_invoices.payment_run.release', user?.permissions)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeufe, setLaeufe] = useState<PaymentRun[]>([])
  const [lauf, setLauf] = useState<PaymentRun | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const gewaehlt = param.get('run')

  const ladeListe = useCallback(async () => {
    try {
      setLaeufe(await fetchPaymentRuns())
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zahlungsläufe ließen sich nicht laden.'))
    }
  }, [])
  useEffect(() => {
    void ladeListe()
  }, [ladeListe])
  useEffect(() => {
    if (!gewaehlt) return setLauf(null)
    let aus = false
    fetchPaymentRun(gewaehlt)
      .then((l) => !aus && setLauf(l))
      .catch((e) => !aus && setFehler(apiErrorText(e, 'Der Zahlungslauf ließ sich nicht laden.')))
    return () => {
      aus = true
    }
  }, [gewaehlt])

  const tue = async (was: () => Promise<PaymentRun>, fehlertext: string) => {
    try {
      setLaeuft(true)
      setFehler(null)
      const l = await was()
      setLauf(l)
      setParam({ run: l.id }, { replace: true })
      await ladeListe()
    } catch (e) {
      setFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Zahlungsläufe</h1>
        <p className="mt-1 text-sm text-muted-foreground">Fällige Rechnungen vorschlagen lassen, als Lauf freigeben und als SEPA-Datei ins Banking laden. Bezahlt ist eine Rechnung erst, wenn der Kontoauszug es bestätigt oder der Lauf als ausgeführt markiert ist.</p>
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {darfAnlegen && <Vorschlag onFehler={setFehler} onLauf={(l) => void tue(async () => l, '')} />}

      <Abschnitt titel="Läufe">
        {laeufe.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch kein Zahlungslauf.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {laeufe.map((l) => (
              <li key={l.id}>
                <button type="button" className={`flex w-full flex-wrap items-center gap-x-4 gap-y-0.5 px-3 py-2 text-left hover:bg-muted/50 ${l.id === gewaehlt ? 'bg-muted/60' : ''}`} onClick={() => setParam(l.id === gewaehlt ? {} : { run: l.id })} aria-expanded={l.id === gewaehlt}>
                  <span className="font-medium tabular-nums">{l.number}</span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${LAUF_STATUS[l.status].klasse}`}>{LAUF_STATUS[l.status].label}</span>
                  <span className="text-muted-foreground">Ausführung {tagText(l.executionDate)}</span>
                  <span className="text-muted-foreground">{l.itemCount} Posten</span>
                  {l.returnedCount > 0 && <span className="text-destructive">{l.returnedCount} Rückläufer</span>}
                  <span className="ml-auto font-medium tabular-nums">{geldText(l.total)}</span>
                </button>
                {l.id === gewaehlt && lauf?.id === l.id && <LaufDetail lauf={lauf} laeuft={laeuft} darfAnlegen={darfAnlegen} darfFreigeben={darfFreigeben} tue={tue} onFehler={setFehler} />}
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>

      <Kontoauszug onFehler={setFehler} onGebucht={() => void ladeListe()} />
    </div>
  )
}

/** Vorschlag: fällig bis zum Stichtag — ankreuzen, was in den Lauf soll. */
function Vorschlag({ onFehler, onLauf }: { onFehler: (t: string | null) => void; onLauf: (l: PaymentRun) => void }) {
  const [bis, setBis] = useState(inTagen(7))
  const [ausfuehrung, setAusfuehrung] = useState(inTagen(1))
  const [v, setV] = useState<RunProposal | null>(null)
  const [wahl, setWahl] = useState<Set<string>>(new Set())
  const [laeuft, setLaeuft] = useState(false)
  const darfBranding = erfuelltRecht('core.branding.manage', useAuthStore((s) => s.user?.permissions))
  // Nachtrag 6, PT-24: Ohne gültige IBAN des eigenen Betriebs entsteht kein Lauf — das steht am Vorschlag, bevor jemand „Lauf anlegen" drückt.
  const auftraggeber = v?.debtorIban?.state ?? 'OK'

  const lade = async () => {
    try {
      setLaeuft(true)
      onFehler(null)
      const r = await fetchRunProposal(bis, ausfuehrung)
      setV(r)
      setWahl(new Set(r.items.map((p) => p.invoiceId)))
    } catch (e) {
      onFehler(apiErrorText(e, 'Der Vorschlag ließ sich nicht laden.'))
    } finally {
      setLaeuft(false)
    }
  }
  const anlegen = async () => {
    if (!v) return
    try {
      setLaeuft(true)
      onFehler(null)
      const l = await createPaymentRun(
        ausfuehrung,
        v.items.filter((p) => wahl.has(p.invoiceId)).map((p) => ({ invoiceId: p.invoiceId, amount: p.amount, discountAmount: p.discountAmount })),
      )
      setV(null)
      onLauf(l)
    } catch (e) {
      onFehler(apiErrorText(e, 'Der Zahlungslauf ließ sich nicht anlegen.'))
    } finally {
      setLaeuft(false)
    }
  }
  const summe = useMemo(() => (v?.items ?? []).filter((p) => wahl.has(p.invoiceId)).reduce((s, p) => s + Math.round(p.amount * 100), 0) / 100, [v, wahl])

  return (
    <Abschnitt titel="Neuer Zahlungslauf">
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="text-xs text-muted-foreground">
          fällig bis
          <input type="date" aria-label="fällig bis" className={`${feldKlasse} mt-0.5 w-40`} value={bis} onChange={(e) => setBis(e.target.value)} />
        </label>
        <label className="text-xs text-muted-foreground">
          Ausführung am
          <input type="date" aria-label="Ausführung am" className={`${feldKlasse} mt-0.5 w-40`} value={ausfuehrung} onChange={(e) => setAusfuehrung(e.target.value)} />
        </label>
        <button type="button" className={knopf} disabled={laeuft || !bis || !ausfuehrung} onClick={() => void lade()}>
          Vorschlag laden
        </button>
      </div>

      {v && (
        <div className="mt-3 space-y-3 text-sm">
          {auftraggeber !== 'OK' && (
            <div role="alert" className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
              {auftraggeber === 'MISSING' ? (
                'Für die Überweisungsdatei fehlt die IBAN des eigenen Betriebs.'
              ) : (
                <>
                  Die IBAN des eigenen Betriebs ist keine gültige IBAN: <span className="font-mono">{v.debtorIban.value}</span> — meist ein Tippfehler, die Prüfziffer stimmt nicht.
                </>
              )}{' '}
              {darfBranding ? (
                <>
                  Sie steht unter{' '}
                  <Link className="underline" to="/settings/branding">
                    Einstellungen → Branding &amp; Layout
                  </Link>
                  , im Abschnitt „Absender".
                </>
              ) : (
                'Ein Administrator trägt sie unter Einstellungen → Branding & Layout im Abschnitt „Absender" ein.'
              )}
            </div>
          )}
          {v.items.length === 0 ? (
            <p className="text-muted-foreground">Bis zum {tagText(bis)} ist nichts zu zahlen.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="w-8 px-3 py-1.5" />
                    <th className="px-2 py-1.5 font-normal">Lieferant</th>
                    <th className="px-2 py-1.5 font-normal">Rechnung</th>
                    <th className="px-2 py-1.5 font-normal">fällig</th>
                    <th className="px-2 py-1.5 text-right font-normal">offen</th>
                    <th className="px-2 py-1.5 text-right font-normal">Skonto</th>
                    <th className="px-3 py-1.5 text-right font-normal">Zahlbetrag</th>
                  </tr>
                </thead>
                <tbody>
                  {v.items.map((p) => (
                    <tr key={p.invoiceId} className="border-t border-border">
                      <td className="px-3 py-1">
                        <input
                          type="checkbox"
                          aria-label={`${p.supplierName ?? ''} ${p.number ?? ''} zahlen`}
                          checked={wahl.has(p.invoiceId)}
                          onChange={(e) => {
                            const neu = new Set(wahl)
                            if (e.target.checked) neu.add(p.invoiceId)
                            else neu.delete(p.invoiceId)
                            setWahl(neu)
                          }}
                        />
                      </td>
                      <td className="px-2 py-1">{p.supplierName}</td>
                      <td className="px-2 py-1">
                        <Link className="underline decoration-dotted hover:text-primary" to={`/incoming-invoices/${p.invoiceId}`}>
                          {p.number}
                        </Link>
                        <span className="ml-2 text-xs text-muted-foreground">{p.supplierInvoiceNumber}</span>
                      </td>
                      <td className="px-2 py-1 tabular-nums">{tagText(p.dueDate)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{geldText(p.open)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{p.discountAmount ? geldText(p.discountAmount) : ''}</td>
                      <td className="px-3 py-1 text-right font-medium tabular-nums">
                        {geldText(p.amount)}
                        {(p.retained > 0 || p.aghAmount > 0) && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {[p.retained > 0 ? `${geldText(p.retained)} einbehalten` : '', p.aghAmount > 0 ? `davon ${geldText(p.aghAmount)} an das Dienstleistungszentrum` : ''].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {v.excluded.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nicht im Vorschlag</p>
              <ul className="mt-1 space-y-0.5">
                {v.excluded.map((p) => (
                  <li key={p.invoiceId} className="flex flex-wrap gap-x-3 text-muted-foreground">
                    <Link className="underline decoration-dotted hover:text-primary" to={`/incoming-invoices/${p.invoiceId}`}>
                      {p.number}
                    </Link>
                    <span>{p.supplierName}</span>
                    <span className="tabular-nums">{geldText(p.open)}</span>
                    <span>— {AUSNAHME[p.reason ?? ''] ?? p.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {v.items.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={haupt} disabled={laeuft || wahl.size === 0 || auftraggeber !== 'OK'} onClick={() => void anlegen()}>
                Lauf anlegen ({wahl.size})
              </button>
              <span className="tabular-nums text-muted-foreground">Summe {geldText(summe)}</span>
            </div>
          )}
        </div>
      )}
    </Abschnitt>
  )
}

function LaufDetail({
  lauf,
  laeuft,
  darfAnlegen,
  darfFreigeben,
  tue,
  onFehler,
}: {
  lauf: PaymentRun
  laeuft: boolean
  darfAnlegen: boolean
  darfFreigeben: boolean
  tue: (was: () => Promise<PaymentRun>, fehlertext: string) => Promise<void>
  onFehler: (t: string | null) => void
}) {
  const entwurf = lauf.status === 'DRAFT'
  const vierAugen = entwurf && lauf.fourEyes && lauf.ownRun
  const aktion = (a: 'release' | 'file' | 'complete' | 'cancel', fehlertext: string) => tue(() => runAction(lauf.id, a), fehlertext)
  const ausgefuehrt = async () => {
    if (!(await bestaetige(`Zahlungslauf ${lauf.number} als ausgeführt markieren? Die geplanten Posten werden als bezahlt gebucht.`, { ok: 'Ausgeführt' }))) return
    await aktion('complete', 'Der Lauf ließ sich nicht abschließen.')
  }
  const verwerfen = async () => {
    if (!(await bestaetige(`Zahlungslauf ${lauf.number} verwerfen? Seine Rechnungen stehen wieder im nächsten Vorschlag.`, { ok: 'Verwerfen', gefaehrlich: true }))) return
    await aktion('cancel', 'Der Lauf ließ sich nicht verwerfen.')
  }
  const laden = async () => {
    try {
      onFehler(null)
      await downloadRunFile(lauf.id, lauf.number)
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Datei ließ sich nicht laden.'))
    }
  }

  return (
    <div className="space-y-3 border-t border-border bg-muted/20 px-3 py-3">
      <p className="text-xs text-muted-foreground">
        Auftraggeberkonto <span className="font-mono">{lauf.debtorIban}</span> · Format {lauf.schemaVersion}
      </p>
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5 font-normal">Empfänger</th>
              <th className="px-2 py-1.5 font-normal">Rechnung</th>
              <th className="px-2 py-1.5 font-normal">IBAN</th>
              <th className="px-2 py-1.5 font-normal">Verwendungszweck</th>
              <th className="px-2 py-1.5 text-right font-normal">Skonto</th>
              <th className="px-2 py-1.5 text-right font-normal">Betrag</th>
              <th className="px-3 py-1.5 font-normal">Stand</th>
            </tr>
          </thead>
          <tbody>
            {(lauf.items ?? []).map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-3 py-1">
                  {p.creditorName}
                  {p.recipient === 'AGH_SERVICE_CENTER' && <span className="ml-2 rounded bg-amber-500/15 px-1.5 text-xs text-amber-700 dark:text-amber-300">Haftungsbetrag</span>}
                </td>
                <td className="px-2 py-1">
                  <Link className="underline decoration-dotted hover:text-primary" to={`/incoming-invoices/${p.invoiceId}`}>
                    {p.invoiceNumber}
                  </Link>
                </td>
                <td className="px-2 py-1 font-mono text-xs">{p.creditorIban}</td>
                <td className="px-2 py-1 text-xs text-muted-foreground">{p.remittanceText}</td>
                <td className="px-2 py-1 text-right tabular-nums">{p.discountAmount ? geldText(p.discountAmount) : ''}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {entwurf && darfAnlegen ? (
                    <BetragFeld posten={p} laufId={lauf.id} onLauf={(l) => void tue(async () => l, '')} />
                  ) : (
                    <span className="font-medium">{geldText(p.amount)}</span>
                  )}
                </td>
                <td className="px-3 py-1">
                  <span className={p.status === 'RETURNED' ? 'text-destructive' : p.status === 'CONFIRMED' ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}>{POSTEN_STATUS[p.status]}</span>
                  {entwurf && darfAnlegen && (lauf.items?.length ?? 0) > 1 && (
                    <button type="button" className="ml-2 rounded p-1 align-middle text-muted-foreground hover:bg-destructive/10 hover:text-destructive" disabled={laeuft} aria-label={`${p.creditorName} aus dem Lauf nehmen`} title="Aus dem Lauf nehmen" onClick={() => void tue(() => removeRunItem(lauf.id, p.id), 'Der Posten ließ sich nicht entfernen.')}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {entwurf && darfFreigeben && (
          <button type="button" className={haupt} disabled={laeuft || !!vierAugen} title={vierAugen ? 'Vier-Augen-Prinzip: Wer den Lauf anlegt, gibt ihn nicht frei' : undefined} aria-label="Lauf freigeben" onClick={() => void aktion('release', 'Der Lauf ließ sich nicht freigeben.')}>
            Lauf freigeben
          </button>
        )}
        {vierAugen && <span className="text-xs text-muted-foreground">Vier-Augen-Prinzip: Diesen Lauf gibt jemand anderer frei.</span>}
        {lauf.status === 'RELEASED' && darfFreigeben && (
          <button type="button" className={haupt} disabled={laeuft} onClick={() => void aktion('file', 'Die SEPA-Datei ließ sich nicht erzeugen.')}>
            SEPA-Datei erzeugen
          </button>
        )}
        {lauf.hasFile && darfFreigeben && (
          <button type="button" className={knopf} onClick={() => void laden()}>
            <Download size={14} /> SEPA-Datei herunterladen
          </button>
        )}
        {lauf.status === 'FILE_CREATED' && darfFreigeben && (
          <button type="button" className={knopf} disabled={laeuft} onClick={() => void ausgefuehrt()}>
            Als ausgeführt markieren
          </button>
        )}
        {['DRAFT', 'RELEASED', 'FILE_CREATED'].includes(lauf.status) && darfAnlegen && lauf.confirmedCount === 0 && (
          <button type="button" className={`${knopf} text-destructive`} disabled={laeuft} onClick={() => void verwerfen()}>
            Lauf verwerfen
          </button>
        )}
      </div>
      {lauf.status === 'FILE_CREATED' && <p className="text-xs text-muted-foreground">Die Datei ins Onlinebanking laden. Bezahlt werden die Posten, sobald du den Kontoauszug einliest — oder den Lauf als ausgeführt markierst.</p>}
    </div>
  )
}

/** Betrag eines Postens im Entwurf — feldweise über `lib/speichern`: speichert beim Verlassen, Esc stellt wieder her. */
function BetragFeld({ posten, laufId, onLauf }: { posten: PaymentRunItem; laufId: string; onLauf: (l: PaymentRun) => void }) {
  // Dasselbe Objekt, solange der Betrag gleich bleibt — die Stelle übernimmt `daten` bei jeder neuen Referenz.
  // Bei jedem Zeichnen neu gebaut, zeichnete der Posten endlos neu, und die Seite stürzte ab (7.10.2026).
  const daten = useMemo(() => ({ amount: posten.amount }), [posten.amount])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `incoming-run-item:${posten.id}`,
    bezeichnung: `Zahlung an ${posten.creditorName}`,
    daten,
    schreibe: async (patch) => {
      const betrag = Number(patch.amount)
      // Skonto bleibt, solange Betrag und Skonto zusammen das Offene nicht übersteigen
      const l = await patchRunItem(laufId, posten.id, betrag, betrag + posten.discountAmount > posten.open ? 0 : posten.discountAmount)
      onLauf(l)
      return { amount: l.items?.find((x) => x.id === posten.id)?.amount ?? betrag }
    },
    werte: (a) => a,
  }) as Speicherstelle<unknown>
  // Preisformat wie jeder Betrag: ohne Fokus 1.234,50, beim Bearbeiten ohne Tausenderpunkt
  const betrag = useFeld<number | null>(stelle, 'amount', { art: 'zahl', schreib: geldEingabe, pflicht: true, label: 'Betrag', pruefe: (w) => (w !== null && w <= 0 ? 'über null' : null) })
  return <GeldFeldFuer aria-label={`Betrag an ${posten.creditorName}`} title={betrag.fehler ?? undefined} className={`${feldKlasse} w-28 ${betrag.fehler ? 'border-destructive' : ''}`} feld={betrag.props} />
}

/** 12.4 — Kontoauszug einlesen: camt.053 ohne Angaben, CSV mit Spaltenzuordnung; dann die Klärungsliste. */
function Kontoauszug({ onFehler, onGebucht }: { onFehler: (t: string | null) => void; onGebucht: () => void }) {
  const wahl = useRef<HTMLInputElement>(null)
  const [datei, setDatei] = useState<File | null>(null)
  const [csv, setCsv] = useState({ delimiter: ';', hasHeader: true, columnReference: '1', columnDate: '2', columnAmount: '3', dateFormat: 'TT.MM.JJJJ', decimalSeparator: ',' })
  const [ergebnis, setErgebnis] = useState<BankImportResult | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const istCsv = !!datei && !/\.xml$/i.test(datei.name)

  const einlesen = async () => {
    if (!datei) return
    try {
      setLaeuft(true)
      onFehler(null)
      const spalte = (s: string) => String(Math.max(0, Number(s) - 1))
      setErgebnis(
        await uploadBankStatement(
          datei,
          istCsv ? { delimiter: csv.delimiter, hasHeader: String(csv.hasHeader), columnReference: spalte(csv.columnReference), columnDate: spalte(csv.columnDate), columnAmount: spalte(csv.columnAmount), dateFormat: csv.dateFormat, decimalSeparator: csv.decimalSeparator } : undefined,
        ),
      )
      setDatei(null)
      if (wahl.current) wahl.current.value = ''
      onGebucht()
    } catch (e) {
      onFehler(apiErrorText(e, 'Der Kontoauszug ließ sich nicht einlesen.'))
    } finally {
      setLaeuft(false)
    }
  }
  const klaere = async (index: number, invoiceId: string | null) => {
    if (!ergebnis?.id) return
    try {
      setLaeuft(true)
      onFehler(null)
      const r = await assignBankEntry(ergebnis.id, index, invoiceId)
      setErgebnis({ ...ergebnis, matched: r.matched, unmatched: r.unmatched })
      onGebucht()
    } catch (e) {
      onFehler(apiErrorText(e, 'Der Umsatz ließ sich nicht zuordnen.'))
    } finally {
      setLaeuft(false)
    }
  }
  const zahl = 'w-16'

  return (
    <Abschnitt titel="Kontoauszug einlesen">
      <p className="mb-2 text-sm text-muted-foreground">Ein Auszug im Format camt.053 (XML) bestätigt die Überweisungen eines Laufs und bucht eindeutige Zahlungen. Eine CSV-Datei — etwa die OP-Liste aus der Buchhaltung — braucht die Angabe, in welcher Spalte Belegnummer, Datum und Betrag stehen.</p>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <input ref={wahl} type="file" aria-label="Kontoauszug wählen" accept=".xml,.csv,.txt,application/xml,text/csv" className="text-sm" onChange={(e) => setDatei(e.target.files?.[0] ?? null)} />
        {istCsv && (
          <>
            <label className="text-xs text-muted-foreground">
              Trennzeichen
              <input aria-label="Trennzeichen" className={`${feldKlasse} mt-0.5 ${zahl}`} value={csv.delimiter} onChange={(e) => setCsv({ ...csv, delimiter: e.target.value })} />
            </label>
            {(
              [
                ['columnReference', 'Spalte Belegnummer'],
                ['columnDate', 'Spalte Datum'],
                ['columnAmount', 'Spalte Betrag'],
              ] as const
            ).map(([k, label]) => (
              <label key={k} className="text-xs text-muted-foreground">
                {label}
                <input aria-label={label} inputMode="numeric" className={`${feldKlasse} mt-0.5 ${zahl}`} value={csv[k]} onChange={(e) => setCsv({ ...csv, [k]: e.target.value })} />
              </label>
            ))}
            <label className="text-xs text-muted-foreground">
              Datum
              <select aria-label="Datumsformat" className={`${feldKlasse} mt-0.5 w-32`} value={csv.dateFormat} onChange={(e) => setCsv({ ...csv, dateFormat: e.target.value })}>
                <option value="TT.MM.JJJJ">TT.MM.JJJJ</option>
                <option value="JJJJ-MM-TT">JJJJ-MM-TT</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">
              Dezimalzeichen
              <select aria-label="Dezimalzeichen" className={`${feldKlasse} mt-0.5 w-20`} value={csv.decimalSeparator} onChange={(e) => setCsv({ ...csv, decimalSeparator: e.target.value })}>
                <option value=",">,</option>
                <option value=".">.</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 pb-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={csv.hasHeader} onChange={(e) => setCsv({ ...csv, hasHeader: e.target.checked })} /> erste Zeile ist die Überschrift
            </label>
          </>
        )}
        <button type="button" className={haupt} disabled={!datei || laeuft} onClick={() => void einlesen()}>
          Einlesen
        </button>
      </div>

      {ergebnis && (
        <div className="mt-3 space-y-2 text-sm">
          <p>
            {ergebnis.matched} {ergebnis.matched === 1 ? 'Zahlung' : 'Zahlungen'} gebucht
            {ergebnis.returned > 0 && <span className="text-destructive"> · {ergebnis.returned} Rückläufer — die Rechnung ist wieder offen</span>}
            {ergebnis.unmatched.length > 0 ? ` · ${ergebnis.unmatched.length} zu klären` : ' · nichts zu klären'}
          </p>
          {ergebnis.unmatched.length > 0 && (
            <ul className="divide-y divide-border rounded-md border border-border">
              {ergebnis.unmatched.map((u) => (
                <li key={u.index} className="px-3 py-2">
                  <div className="flex flex-wrap gap-x-3">
                    <span className="tabular-nums">{tagText(u.date)}</span>
                    <span className="font-medium tabular-nums">{geldText(u.amount)}</span>
                    <span>{u.name ?? u.reference}</span>
                    {u.iban && <span className="font-mono text-xs text-muted-foreground">{u.iban}</span>}
                    <span className="text-muted-foreground">{u.text}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {u.candidates.map((k) => (
                      <button key={k.id} type="button" className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted" disabled={laeuft} onClick={() => void klaere(u.index, k.id)}>
                        {k.number} · {k.supplierName} · {k.supplierInvoiceNumber} · offen {geldText(k.open)}
                      </button>
                    ))}
                    {u.candidates.length === 0 && <span className="text-xs text-muted-foreground">Keine offene Rechnung passt — die Zahlung an der Rechnung von Hand erfassen.</span>}
                    <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" disabled={laeuft} onClick={() => void klaere(u.index, null)}>
                      gehört nicht dazu
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Abschnitt>
  )
}
